import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/shared/ui/organisms/DataTable';
import type { PersonResume, ResumeEnrollment } from '../../../types/resume';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';

export interface PersonResumeSummaryProps {
  readonly resume: PersonResume;
}

export function PersonResumeSummary({ resume }: PersonResumeSummaryProps) {
  const { t } = useTranslation('personManagement');

  const enrollmentColumns = useMemo<ColumnDef<ResumeEnrollment, unknown>[]>(
    () => [
      {
        accessorKey: 'courseName',
        header: t('table.columns.course'),
        cell: info => (
          <span className="ts-paragraph-xs !font-medium">{info.getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'className',
        header: t('table.columns.className'),
        cell: info => <span className="ts-paragraph-xs">{info.getValue() as string}</span>,
      },
      {
        id: 'status',
        header: t('table.columns.status'),
        cell: ({ row }) => <EnrollmentStatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'grade',
        header: t('table.columns.grade'),
        cell: info => {
          const grade = info.getValue() as number | null;
          return <span className="ts-paragraph-xs">{grade !== null ? grade.toFixed(1) : '—'}</span>;
        },
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-sm">
      {/* Personal Info */}
      <section className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs shadow-level1">
        <h2 className="mb-mini ts-subtitle-sm text-ctx-content-title">
          {t('resumePage.sections.personalInfo')}
        </h2>
        <div className="grid grid-cols-1 gap-mini sm:grid-cols-2">
          <InfoRow label={t('personForm.fields.firstName')} value={resume.fullName} />
        </div>
      </section>

      {/* Company Info */}
      <section className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs shadow-level1">
        <h2 className="mb-mini ts-subtitle-sm text-ctx-content-title">
          {t('resumePage.sections.companyInfo')}
        </h2>
        {(resume.companyProfile ?? resume.companyUnit) ? (
          <div className="grid grid-cols-1 gap-xs sm:grid-cols-2">
            <InfoRow label="Perfil" value={resume.companyProfile ?? '—'} />
            <InfoRow label="Unidade" value={resume.companyUnit ?? '—'} />
          </div>
        ) : (
          <p className="ts-paragraph-xs text-ctx-content-placeholder">
            {t('resumePage.sections.noCompany')}
          </p>
        )}
      </section>

      {/* Enrollments */}
      <section className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs shadow-level1">
        <h2 className="mb-mini ts-subtitle-sm text-ctx-content-title">
          {t('resumePage.sections.enrollments')}
        </h2>
        {resume.enrollments.length > 0 ? (
          <DataTable
            data={resume.enrollments as ResumeEnrollment[]}
            columns={enrollmentColumns}
            totalCount={resume.enrollments.length}
          />
        ) : (
          <p className="ts-paragraph-xs text-ctx-content-placeholder">
            {t('resumePage.sections.noEnrollments')}
          </p>
        )}
      </section>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs text-ctx-content-placeholder">{label}</span>
      <span className="ts-paragraph-xs text-ctx-content-base">{value}</span>
    </div>
  );
}
